import React from "react";
import {observer} from "mobx-react-lite";
import {generatePath, useNavigate} from "react-router";
import {useStores} from "../../../stores";
import {routes} from "../../../routing/routes.ts";
import ChatStore from "../../../pages/chat-agent/ChatStore.ts";
import {ChatInput} from "../../../pages/chat-agent/components/ChatInput/ChatInput";
import {useChatContext} from "../../../pages/chat-agent/context/ChatContext.tsx";
import {useChatAvailableForActiveDatabag} from "../../../pages/chat-agent/hooks/useChatAvailableForActiveDatabag";
import AtlasIcon from "../../../components/icons/svg/AtlasIcon.svg?react";
import {BRIEFING} from "../briefingData";

/**
 * The Atlas hero at the top of the Daily Briefing: gradient card greeting the
 * briefing's persona, with the ask-anything input. Submitting starts a real
 * chat and navigates to the Atlas page (the fixture-seeded companion panel
 * stays on "Ask Atlas" below).
 */
export const AtlasHero: React.FC = observer(() => {
    const {p, bagStore} = useStores();
    const {startNewChat, isWaiting} = useChatContext();
    const chatDatabagAvailability = useChatAvailableForActiveDatabag();
    const navigate = useNavigate();

    const visible = p.showCard('chat')
        && !p.isDisabledCard('chat', bagStore.bag).disabled
        && (!chatDatabagAvailability.isReady || chatDatabagAvailability.isAvailable);
    if (!visible) return null;

    // The briefing belongs to a persona (Jon de Vries; switchable to Priya Nair),
    // so Atlas greets that persona rather than the signed-in account.
    const firstName = BRIEFING.session.user.name.split(' ')[0];

    const handleSubmit = async (value: string) => {
        const trimmedValue = value.trim();
        const bagId = bagStore.bagId;
        if (!trimmedValue || !bagId) {
            return;
        }
        ChatStore.setUserInputValue(trimmedValue);
        if (isWaiting) {
            ChatStore.setUserInputValue(trimmedValue);
        } else {
            startNewChat(trimmedValue, bagId);
            ChatStore.setUserInputValue("");
        }
        navigate(generatePath(routes.chat, {id: String(bagId)}));
    };

    return (
        <section className="atlas-hero" aria-label="Ask Atlas about your spend">
            <div className="atlas-hero-head">
                <AtlasIcon className="atlas-hero-mark"/>
                <span className="atlas-hero-brand">Atlas</span>
                <span className="atlas-hero-badge">AI</span>
            </div>
            <h2 className="atlas-hero-title">
                Hi {firstName}, <b>what's on your mind today?</b>
            </h2>
            <div className="atlas-hero-input-wrap">
                <ChatInput
                    conversationStarted={false}
                    defaultPosition="start"
                    disabled={!bagStore.bagId}
                    newChatDraftOnly
                    showChatSwitchLoading={false}
                    showIntro={false}
                    onSubmit={handleSubmit}
                />
            </div>
        </section>
    );
});
